import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Language_i18 from '../../js/useNationalLanguage.jsx';
import { TabsUiItem } from './tabs_ui.jsx';
import localNavConfig from './navConfig.json';
import { isLocalHost } from '../../shared/host_utils.js';

function ProfileNav({ category, slug_1: slug1Override, slug_2: slug2Override, slug_3: slug3Override, slug_4: slug4Override }) {
    const isLocalhost =
        typeof window !== 'undefined' && isLocalHost(window.location.hostname);
    const apiBase =
        isLocalhost
            ? `http://${window.location.hostname}:3003`
            : 'https://hust.media';
    const TABS_NAV_API_URL = `${apiBase}/next/tabs_nav/tabs_nav.json`;
    const TABS_NAV_STORAGE_KEY = 'tabs_nav_config_cache_v1';
    const TABS_NAV_CACHE_MAX_AGE_MS = 14 * 60 * 60 * 1000;
    const navigate = useNavigate();
    // Thêm slug_4 cho level 4
    const params = useParams();
    const slug_1 = slug1Override ?? params.slug_1;
    const slug_2 = slug2Override ?? params.slug_2;
    const slug_3 = slug3Override ?? params.slug_3;
    const slug_4 = slug4Override ?? params.slug_4;
    const { lang } = Language_i18();
    const [navConfig, setNavConfig] = useState([]);
    const [isConfigReady, setIsConfigReady] = useState(false);

    useEffect(() => {
        let isCancelled = false;

        const setConfigIfValid = (data) => {
            if (Array.isArray(data) && !isCancelled) {
                setNavConfig(data);
                return true;
            }
            return false;
        };

        const loadTabsConfig = async () => {
            let hasCachedData = false;
            let shouldRefreshFromApi = true;
            const now = Date.now();

            try {
                const cachedRaw = localStorage.getItem(TABS_NAV_STORAGE_KEY);
                if (cachedRaw) {
                    const cachedParsed = JSON.parse(cachedRaw);
                    if (Array.isArray(cachedParsed)) {
                        // Cache legacy không có timestamp: dùng tạm rồi refresh ngay
                        hasCachedData = setConfigIfValid(cachedParsed);
                        shouldRefreshFromApi = true;
                    } else if (cachedParsed && Array.isArray(cachedParsed.data)) {
                        hasCachedData = setConfigIfValid(cachedParsed.data);
                        const savedAt = Number(cachedParsed.savedAt) || 0;
                        const ageMs = savedAt > 0 ? now - savedAt : Number.POSITIVE_INFINITY;
                        shouldRefreshFromApi = isLocalhost || ageMs >= TABS_NAV_CACHE_MAX_AGE_MS;
                    }
                }
            } catch (error) {
                console.error('Cannot parse tabs nav cache:', error);
            }

            if (hasCachedData && !shouldRefreshFromApi) {
                if (!isCancelled) {
                    setIsConfigReady(true);
                }
                return;
            }

            try {
                const response = await fetch(TABS_NAV_API_URL, { cache: 'no-store' });
                if (!response.ok) {
                    throw new Error(`HTTP ${response.status}`);
                }
                const apiData = await response.json();
                if (setConfigIfValid(apiData)) {
                    localStorage.setItem(
                        TABS_NAV_STORAGE_KEY,
                        JSON.stringify({
                            savedAt: Date.now(),
                            data: apiData,
                        })
                    );
                } else if (!hasCachedData && !isCancelled) {
                    setConfigIfValid(localNavConfig);
                }
            } catch (error) {
                console.error('Cannot fetch tabs nav config:', error);
                if (!hasCachedData && !isCancelled) {
                    setConfigIfValid(localNavConfig);
                }
            } finally {
                if (!isCancelled) {
                    setIsConfigReady(true);
                }
            }
        };

        loadTabsConfig();
        return () => {
            isCancelled = true;
        };
    }, []);
    const isEmptySlug = (value) => value === null || value === undefined || value === '';
    const matchesSlug = (configValue, currentValue) => {
        if (Array.isArray(configValue)) {
            return configValue.includes(currentValue);
        }
        if (isEmptySlug(configValue)) {
            return isEmptySlug(currentValue);
        }
        return configValue == currentValue;
    };
    const matchesChildSlug = (child, currentValue, fallbackValue) => {
        if (!isEmptySlug(child.slug_2) && matchesSlug(child.slug_2, currentValue)) {
            return true;
        }
        if (!isEmptySlug(child.slug_1) && matchesSlug(child.slug_1, currentValue)) {
            return true;
        }
        if (!isEmptySlug(child.slug_1) && !isEmptySlug(fallbackValue) && matchesSlug(child.slug_1, fallbackValue)) {
            return true;
        }
        return false;
    };
    const getItemKey = (item) => {
        if (Array.isArray(item.slug_1)) {
            return item.slug_1.join('|');
        }
        return item.slug_1;
    };
    const getChildKey = (child) => {
        if (!isEmptySlug(child.slug_2)) {
            return child.slug_2;
        }
        if (!isEmptySlug(child.slug_1)) {
            return child.slug_1;
        }
        return child.path;
    };
    const getSubKey = (sub) => {
        if (!isEmptySlug(sub.slug_3)) {
            return sub.slug_3;
        }
        if (!isEmptySlug(sub.slug_2)) {
            return sub.slug_2;
        }
        return sub.path;
    };
    const getSub2Key = (sub2) => {
        if (!isEmptySlug(sub2.slug_4)) {
            return sub2.slug_4;
        }
        if (!isEmptySlug(sub2.slug_3)) {
            return sub2.slug_3;
        }
        return sub2.path;
    };
    const isSubActive = (sub) => {

        if (!isEmptySlug(sub.slug_3)) {
            return matchesSlug(sub.slug_3, slug_3);
        }
        if (!isEmptySlug(sub.slug_2)) {
            return matchesSlug(sub.slug_2, slug_2);
        }
        if (!isEmptySlug(slug_3)) {
            return false;
        }
        return matchesSlug(sub.slug_3, slug_3);
    };
    const isSub2Active = (sub2) => {
        if (!isEmptySlug(sub2.slug_4)) {
            return matchesSlug(sub2.slug_4, slug_4);
        }
        if (!isEmptySlug(sub2.slug_3)) {
            return matchesSlug(sub2.slug_3, slug_3);
        }
        return false;
    };

    if (!isConfigReady && navConfig.length === 0) {
        return <div>Đang tải menu...</div>;
    }

    if (!navConfig || navConfig.length === 0) {
        return <div>Không tìm thấy json</div>;
    }

    const categoryConfig = navConfig.find(cat => cat.category == category);
    if (!categoryConfig) {
        return <div>Không tìm thấy danh mục: {category}</div>;
    }
    return (
        <div className="flex flex-col space-y-4">
            {/* Level 1 */}
            <div className="flex space-x-4 justify-center">
                {categoryConfig.items.map((item) => (
                    <TabsUiItem
                        key={getItemKey(item)}
                        active={matchesSlug(item.slug_1, slug_1)}
                        onClick={() => {
                            if (item.path?.startsWith('http')) {
                                window.open(item.path, '_blank');
                            } else if (item.path) {
                                navigate(item.path);
                            } else if (item.children?.length > 0) {
                                navigate(item.children[0].path);
                            }
                        }}
                        label={item.label[lang] || item.label.vi}
                    />
                ))}
            </div>

            {/* Level 2 */}
            {categoryConfig.items.map((item) =>
                item.children && matchesSlug(item.slug_1, slug_1) ? (
                    <div key={getItemKey(item)} className="flex flex-col space-y-2 mt-2 items-center w-full">
                        <div className="flex space-x-4 justify-center w-full">
                            {item.children.map((child) => (
                                <TabsUiItem
                                    key={getChildKey(child)}
                                    active={matchesChildSlug(child, slug_2, slug_1)}
                                    onClick={() => {
                                        if (child.path?.startsWith('http')) {
                                            window.open(child.path, '_blank');
                                        } else if (child.path) {
                                            navigate(child.path);
                                        } else if (child.children?.length > 0) {
                                            navigate(child.children[0].path);
                                        }
                                    }}
                                    label={child.label[lang] || child.label.vi}
                                />
                            ))}
                        </div>

                        {/* Level 3 */}
                        {item.children.map((child) =>
                            child.children && matchesChildSlug(child, slug_2, slug_1) ? (
                                <div key={getChildKey(child) + '-sub'} className="flex flex-col space-y-2 mt-2 items-center w-full">
                                    <div className="flex space-x-4 justify-center w-full">
                                        {child.children.map((sub) => (
                                            <TabsUiItem
                                                key={getSubKey(sub)}
                                                active={isSubActive(sub)}
                                                onClick={() => {
                                                    if (sub.path?.startsWith('http')) {
                                                        window.open(sub.path, '_blank');
                                                    } else if (sub.path) {
                                                        navigate(sub.path);
                                                    }
                                                }}
                                                label={sub.label[lang] || sub.label.vi}
                                            />
                                        ))}
                                    </div>

                                    {/* Level 4 */}
                                    {child.children.map((sub) =>
                                        sub.children && isSubActive(sub) ? (
                                            <div key={sub.slug_3 + '-sub2'} className="flex space-x-4 mt-2 justify-center w-full">
                                                {sub.children.map((sub2) => (
                                                    <TabsUiItem
                                                        key={getSub2Key(sub2)}
                                                        active={isSub2Active(sub2)}
                                                        onClick={() => {
                                                            if (sub2.path?.startsWith('http')) {
                                                                window.open(sub2.path, '_blank');
                                                            } else if (sub2.path) {
                                                                navigate(sub2.path);
                                                            }
                                                        }}
                                                        label={sub2.label[lang] || sub2.label.vi}
                                                    />
                                                ))}
                                            </div>
                                        ) : null
                                    )}
                                </div>
                            ) : null
                        )}
                    </div>
                ) : null
            )}
        </div>

    );
}

export default ProfileNav;
